import { get, isNil } from 'lodash-es'
import { findEpiSuiteGroupById } from '@/components/combined-predict/models/epiSuite/epiSuiteColumnGroups'
import { buildDisplayText, formatModelValue, getSummaryValue, idToTitle, makeFactorTable } from '../utils'

const summaryMapper = (item) => {
  const halfLives = item?.halfLives ?? []
  const rateConstantKeys = Object.keys(item ?? {}).filter((key) => key.endsWith('Constant'))

  const nonZeroHalfLives = halfLives.filter((halfLife) => !isNil(halfLife?.value) && Number(halfLife.value) !== 0)

  const getHalfLifeType = (halfLife) => {
    const activeKey = Object.entries(halfLife ?? {}).find(([, value]) => value === true)?.[0]
    return activeKey ? idToTitle(activeKey) : null
  }

  const getHalfLifeTrueKeys = (halfLife) =>
    Object.entries(halfLife ?? {})
      .filter(([, value]) => value === true)
      .map(([key]) => key)

  const getRateConstantType = (halfLife) => {
    const activeKeys = getHalfLifeTrueKeys(halfLife)

    if (!activeKeys.length) return null
    if (activeKeys.length > 1) return 'neutral'

    return activeKeys[0]
  }

  const getRateConstantKey = (halfLife) => {
    const rateConstantType = getRateConstantType(halfLife)
    if (!rateConstantType) return null

    return rateConstantKeys.find((key) => key === `${rateConstantType}RateConstant`) ?? null
  }

  const isNoValuesAvailable = !nonZeroHalfLives.length

  const summaryValues = nonZeroHalfLives
    .map((halfLife) => {
      const halfLifeType = getHalfLifeType(halfLife)
      if (!halfLifeType) return null

      return getSummaryValue(
        {
          value: halfLife?.value,
          unit: halfLife?.unit,
        },
        `Est. ${halfLifeType} at pH${halfLife?.ph}`,
      )
    })
    .filter(Boolean)

  const buildRateConstantCell = (halfLife) => {
    const rateConstant = get(item, getRateConstantKey(halfLife))

    return buildDisplayText({
      mainText: formatModelValue(rateConstant),
      secondaryText: !isNil(rateConstant) ? 'L/mol-sec at 25°C' : null,
    })
  }

  const buildHalfLifeCell = (halfLife) => {
    const phText = !isNil(halfLife?.ph) ? `@ pH${halfLife.ph}` : null

    return buildDisplayText({
      mainText: formatModelValue(halfLife?.value),
      secondaryText: halfLife?.unit ? `${halfLife.unit}${phText ? ` ${phText}` : ''}` : null,
    })
  }

  const rows = nonZeroHalfLives.map((halfLife) => {
    const rateConstantType = getRateConstantType(halfLife)

    return [
      rateConstantType ? idToTitle(rateConstantType) : null,
      buildRateConstantCell(halfLife),
      buildHalfLifeCell(halfLife),
    ]
  })

  return {
    values: isNoValuesAvailable ? [getSummaryValue({ value: 'N/A' })] : summaryValues,
    tables: rows.length ? [{ headers: ['Type', 'Rate Constant', 'Half Life'], rows }] : null,
    notes: isNoValuesAvailable
      ? 'Half-lives are currently unavailable for the specified chemical. Please see the Model Output tab for more details.'
      : null,
  }
}

const descriptorsMapper = (item) => {
  const fragmentsTable = makeFactorTable(item?.fragments)

  return {
    ...(!fragmentsTable
      ? {
          notes: 'No hydrolyzable functions were detected. Please see the Model Output tab for more details.',
        }
      : {
          tables: [fragmentsTable],
        }),
  }
}

const hydroWin = (items) => {
  const item = items?.find((i) => i?.property === 'hydrolysis')
  const meta = findEpiSuiteGroupById('hydrolysis')

  return {
    title: meta?.name,
    description: 'Aqueous Hydrolysis Half Lives',
    helpPage: meta?.helpPage,
    summary: summaryMapper(item),
    model: descriptorsMapper(item),
    modelOutput: item?.output ?? null,
  }
}

export { hydroWin }
